import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface PublishState {
  contentType: 'dashreel';
  details: {
    title: string;
    creatorName: string;
    description: string;
    language: string;
    showType: string;
    genres: string[];
    tropes: string[];
    tags: string[];
    publishFrequency: number;
    maturityRating: string;
    isTrialShow: boolean;
    isFastPassEnabled: boolean;
  };
  internalDetails: {
    actorEthnicity: string;
    sourceSubtitleLanguage: string;
    trailerLinks: string[];
    leadCharacterGender: string;
    targetAudienceGender: string;
    sourceLanguage: string;
  };
  promoDetails: {
    promos: Array<{
      id?: string;
      level: string;
      writerName: string;
      producerName: string;
      promosTested: string[];
      bestCpi: number;
      percentMaleInCpi: number;
      bestCpt: number;
      percentMaleInCpt: number;
      promosWithCpiLessThan2: string[];
      promosWithCptLessThan50: string[];
    }>;
    successfulPromoLinks: string[];
    promoPerformanceDetails: string;
    outsourcedShow?: any;
  };
  dubbingDetails: {
    projects: Array<{
      id?: string;
      showId: string;
      readyForLocalization: boolean;
      showAssignedDate?: string;
      growthPriority: number | null;
      scrapeStatus: boolean;
      srtStatus: boolean;
      localizedVersions: Array<{
        language: string;
        status: 'NOT_STARTED' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'RELEASED';
        dubbingInfo?: {
          studioAssigned: string;
          assignedDate: string;
          stages: 'SCRIPTING' | 'CASTING' | 'RECORDING' | 'MIXING' | 'FINALIZING' | 'COMPLETED';
          notes?: string;
        };
        writerInfo?: {
          agencyType: string;
          name: string;
          stages: 'NOT_STARTED' | 'FIRST_DRAFT' | 'REVIEW' | 'REWORKING' | 'FINAL_VERSION';
          assignedDate?: string;
        };
        qcInfo?: {
          type: string;
          qcAgency?: string;
          qcDone: boolean;
          passedStatus: 'PASSED' | 'FAILED' | 'PENDING';
          owner?: string;
          remarksSheetLink?: string;
        };
        dubbedLinkFinal?: string;
        localizationSheetLink?: string;
        nameChangeInfo?: {
          changedToLocalizedName?: string;
          status: 'NOT_APPLICABLE' | 'NOT_STARTED' | 'PARTIALLY_COMPLETED' | 'COMPLETED';
        };
        missingAdditionalCharacters?: string[];
        localizedEpsDriveLink?: string;
      }>;
    }>;
  };
  thumbnails: {
    homeScreenBanner?: File | null;
    homeScreenWidget?: File | null;
    detailPage?: File | null;
    continueReading?: File | null;
    reelsThumbnail4x5?: File | null;
    reelsThumbnail5x8?: File | null;
  };
  content: {
    files: File[];
  };
  extras: {
    postCreditThumbnails: File[];
    endCreditsContent: File[];
  };
}

interface PublishContextType {
  state: PublishState;
  updateState: (updates: Partial<PublishState>) => void;
  resetState: () => void;
}

const initialState: PublishState = {
  contentType: 'dashreel',
  details: {
    title: '',
    creatorName: '',
    description: '',
    language: '',
    showType: '',
    genres: [],
    tropes: [],
    tags: [],
    publishFrequency: 7,
    maturityRating: '',
    isTrialShow: false,
    isFastPassEnabled: false
  },
  internalDetails: {
    actorEthnicity: '',
    sourceSubtitleLanguage: '',
    trailerLinks: [],
    leadCharacterGender: '',
    targetAudienceGender: '',
    sourceLanguage: ''
  },
  promoDetails: {
    promos: [],
    successfulPromoLinks: [],
    promoPerformanceDetails: '',
    outsourcedShow: null
  },
  dubbingDetails: {
    projects: []
  },
  thumbnails: {},
  content: {
    files: []
  },
  extras: {
    postCreditThumbnails: [],
    endCreditsContent: []
  }
};

const PublishContext = createContext<PublishContextType | undefined>(undefined);

export function PublishProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PublishState>(initialState);
  const location = useLocation();

  // Load state from sessionStorage on mount and route changes
  useEffect(() => {
    const isPublishRoute = location.pathname.startsWith('/publish');
    
    if (isPublishRoute) {
      const savedState = sessionStorage.getItem('publishState');
      if (savedState) {
        try {
          const parsed = JSON.parse(savedState);
          setState(parsed);
        } catch (error) {
          console.error('Error parsing saved publish state:', error);
          sessionStorage.removeItem('publishState');
        }
      }
    } else if (!location.pathname.startsWith('/publish')) {
      // Clear state when leaving publish flow
      setState(initialState);
      sessionStorage.removeItem('publishState');
    }
  }, [location.pathname]);

  // Save state to sessionStorage on change
  useEffect(() => {
    if (state !== initialState && location.pathname.startsWith('/publish')) {
      sessionStorage.setItem('publishState', JSON.stringify(state));
    }
  }, [state, location.pathname]);

  const updateState = (updates: Partial<PublishState>) => {
    console.log('Updating publish state:', updates);
    setState(prev => ({
      ...prev,
      ...updates,
      details: {
        ...prev.details,
        ...(updates.details || {})
      },
      internalDetails: {
        ...prev.internalDetails,
        ...(updates.internalDetails || {})
      },
      promoDetails: {
        ...prev.promoDetails,
        ...(updates.promoDetails || {})
      },
      dubbingDetails: {
        ...prev.dubbingDetails,
        ...(updates.dubbingDetails || {})
      },
      thumbnails: {
        ...prev.thumbnails,
        ...(updates.thumbnails || {})
      },
      content: {
        ...prev.content,
        ...(updates.content || {})
      },
      extras: {
        ...prev.extras,
        ...(updates.extras || {})
      }
    }));
  };

  const resetState = () => {
    setState(initialState);
    sessionStorage.removeItem('publishState');
  };

  // Listen for navigation events
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (state !== initialState) {
        sessionStorage.setItem('publishState', JSON.stringify(state));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state]);

  return (
    <PublishContext.Provider value={{ state, updateState, resetState }}>
      {children}
    </PublishContext.Provider>
  );
}

export function usePublish() {
  const context = useContext(PublishContext);
  if (context === undefined) {
    throw new Error('usePublish must be used within a PublishProvider');
  }
  return context;
}